import { bind } from '@react-rxjs/core';
import { createSignal } from '@react-rxjs/utils';
import { EMPTY, merge, of, timer } from 'rxjs';
import { bufferCount, catchError, distinctUntilChanged, map, mergeMap, switchMap, take, takeUntil, tap } from 'rxjs';
import { queueNotice } from '../../components/Notice';
import { Credentials, Token, authenticate } from '../../services/authentication';
import { ResponseError, request } from '../../services/request';
import { convertToAsyncTaskState } from '../../utils/rxjs/task';

const emailRegex =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i;

export const handleBackendError = (e: ResponseError) => {
  if (e.response?.status === 400) {
    const err = new Error();
    throw Object.assign(err, {
      password: 'Login.CredentialsError',
      username: 'Login.CredentialsError',
    });
  } else {
    const message = 'Login.Error';
    queueNotice({ message });
    throw new Error(message);
  }
};

type FormErrors<T> = Partial<Record<keyof T, string>>;
export type LoginFormErrors = FormErrors<Credentials>;

const validateForm = (form: Credentials): void => {
  const err: LoginFormErrors = {};
  if (!form.password) {
    err.password = 'Password.Required';
  }

  if (!form.username) {
    err.username = 'Email.Required';
  }

  if (form.username && !emailRegex.test(form.username)) {
    err.username = 'Email.Validity';
  }
  if (err.password || err.username) {
    throw err;
  }
};

export const [logout$, logout] = createSignal();
export const [sessionExpired$, expireSession] = createSignal();
export const [credentials$, setCredentials] = createSignal<Credentials>();
const validation$ = credentials$.pipe(
  switchMap((form) =>
    of(form).pipe(
      tap(validateForm),
      switchMap((form) => authenticate(form).pipe(catchError(handleBackendError))),
      convertToAsyncTaskState<Token, LoginFormErrors>()
    )
  )
);

export const logoutRequest$ = logout$.pipe(
  switchMap(() =>
    request('/gateway/auth/logout', { method: 'POST' }).pipe(
      map(() => null),
      tap(() => window.location.reload()),
      catchError(() => EMPTY)
    )
  )
);

export const [useLogin, login$] = bind(validation$, { error: null, result: null, running: false });

const QA_TIMES_TO_CLICK = 7;
const QA_TIME_WINDOW_TO_CLICK = 2000;

export const [clickLogo$, clickLogo] = createSignal();
export const [closeQAMenu$, closeQAMenu] = createSignal();

export const [useQAMenu] = bind(
  merge(
    closeQAMenu$.pipe(map(() => false)),
    clickLogo$.pipe(
      mergeMap(() =>
        clickLogo$.pipe(
          bufferCount(QA_TIMES_TO_CLICK - 1),
          takeUntil(timer(QA_TIME_WINDOW_TO_CLICK)),
          take(1),
          map(() => true)
        )
      )
    )
  ).pipe(distinctUntilChanged()),
  false
);
